import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import EditCommentary from "./EditCommentary";
import { connect } from "react-redux";
import genericProfilePic from "../genericprofilepic.png";
import { notifyAllUsers } from "../utils/notificationHelpers";

function Commentary(props) {
  const getInitialCommentary = () => ({
    user_id: localStorage.getItem("user_id"),
    book: props.book,
    chapter: props.chapter,
    commentary: "",
  });

  const [theCommentary, setTheCommentary] = useState(getInitialCommentary);
  const [visibleCommentary, setVisibleCommentary] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const fetchCommentaries = async () => {
    // Read userId fresh each time — avoids stale closure from initial render
    const userId = localStorage.getItem("user_id");
    if (!userId) return;
    try {
      const snapshot = await getDocs(
        query(collection(db, 'commentaries'), where('user_id', '==', userId))
      );
      setVisibleCommentary(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to fetch commentaries:", err);
    }
  };

  // Re-fetch when book or chapter changes (navigation between chapters)
  useEffect(() => {
    fetchCommentaries();
  }, [props.book, props.chapter]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(e) {
    setTheCommentary({
      ...theCommentary,
      [e.target.name]: e.target.value,
    });
  }

  const postCommentary = async (e) => {
    e.preventDefault();
    setSaveError(null);

    // Read userId fresh at post time — avoids stale closure issues
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      setSaveError("You must be logged in to save commentary.");
      return;
    }
    if (!theCommentary.commentary.trim() || saving) return;
    setSaving(true);

    const commentaryToSave = {
      user_id: userId,
      book: props.book,
      chapter: props.chapter,
      commentary: theCommentary.commentary.trim(),
      created_at: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, 'commentaries'), commentaryToSave);
      // Optimistic update: add to local state immediately so it shows up right away
      setVisibleCommentary(prev => [...prev, { id: docRef.id, ...commentaryToSave, created_at: new Date() }]);
      setTheCommentary(getInitialCommentary());

      const fromName = `${props.first_name || ''} ${props.last_name || ''}`.trim() || 'Someone';
      const snippet = theCommentary.commentary.length > 80
        ? theCommentary.commentary.slice(0, 80) + '…'
        : theCommentary.commentary;
      notifyAllUsers({
        fromUserId: userId,
        fromName,
        type: 'bible_commentary',
        message: `${fromName} added commentary on ${props.book} chapter ${props.chapter}: "${snippet}"`,
        url: `/${props.apibook}${props.chapter}`,
      }).catch(() => {});
    } catch (err) {
      console.error("Failed to save commentary:", err);
      setSaveError("Failed to save. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCommentary = async (id) => {
    if (!window.confirm("Delete this commentary?")) return;
    try {
      await deleteDoc(doc(db, 'commentaries', id));
      setVisibleCommentary(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error("Failed to delete commentary:", err);
    }
  };

  const filteredCommentary = visibleCommentary.filter(
    (commentary) =>
      commentary.book === props.book && commentary.chapter === props.chapter
  );

  return (
    <div>
      <br />
      <div id="pic-mycommentary">
        <img
          id="smallprofilepic"
          src={props.profile_pic_url || genericProfilePic}
          alt="profile"
        ></img>
        <br />
        <h3 className="my-commentary-title">My Commentary:</h3>
      </div>
      <div>
        {filteredCommentary.map((commentary) =>
          editingId === commentary.id ? (
            <EditCommentary
              setShowEditForm={() => setEditingId(null)}
              key={commentary.id}
              commentary={commentary.commentary}
              commId={commentary.id}
              book={props.book}
              apibook={props.apibook}
              chapter={props.chapter}
              onSaved={fetchCommentaries}
            />
          ) : (
            <div className="roundedbox" key={commentary.id}>
              <p className="commentary-text">{commentary.commentary}</p>
              <button onClick={() => setEditingId(commentary.id)}>Edit</button>
              <button onClick={() => deleteCommentary(commentary.id)}>
                Delete
              </button>
            </div>
          )
        )}
      </div>
      {saveError && (
        <p style={{ color: "red", margin: "8px 16px" }}>{saveError}</p>
      )}
      <form onSubmit={postCommentary} id="commentary">
        <textarea
          name="commentary"
          form="commentary"
          value={theCommentary.commentary}
          onChange={handleChange}
        ></textarea>
        <button id="bigbutton" type="submit" disabled={saving || !theCommentary.commentary.trim()}>
          {saving ? "Saving…" : "Save My Commentary"}
        </button>
      </form>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    first_name: state.first_name,
    last_name: state.last_name,
    profile_pic_url: state.profile_pic_url,
  };
};

export default connect(mapStateToProps, {})(Commentary);
